/**
 * 项目总览页面
 * Feature: mobile-responsive-framework - 应用响应式框架
 * Feature: real-time-refresh - 实时数据刷新
 * Feature: project-centric-view - 项目为中心的视图
 * Feature: dual-view-mode - 双视图切换（卡片/表格/列表）
 * Feature: project-overview-modal - 项目概览弹窗
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Alert, Space, Input,
  Progress, Tag, Button, Select, Divider, Badge, Modal, message,
  Segmented, Tooltip
} from 'antd';
import {
  ProjectOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ShoppingOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  TeamOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  BarsOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  FundOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { StatisticsService, DashboardFocusItem } from '../../services/statisticsService';
import { DashboardService, ProjectOverviewData } from '../../services/dashboardService';
import { ProjectService } from '../../services/projectService';
import { Project } from '../../types';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { ResponsiveContainer } from '../../responsive/components/ResponsiveContainer';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useDataChangeDetector } from '../../hooks/useDataChangeDetector';
import { RefreshControl, DataChangeIndicator } from '../../components/RefreshControl/RefreshControl';
import {
  CompactProjectCard,
  ProjectTableView,
  CollapsibleProjectList,
  ProjectSkeleton
} from '../../components/ProjectViews';
import ProjectOverviewModal from '../../components/ProjectOverviewModal';
import ProjectForm from '../Project/ProjectForm';
import WorkTimeForm from '../WorkTime/WorkTimeForm';
import EquipmentUsageForm from '../Equipment/EquipmentUsageForm';
import MaterialUsageForm from '../Material/MaterialUsageForm';
import WorkerHistoryForm from '../../components/WorkerHistoryForm';
import { getBudgetBasedColors } from '../../utils/colorUtils';
import ProjectControlCenter from '../Project/ControlCenter';
import { useAuthStore } from '../../store/authStore';
import { hasSensitivePermission } from '../../utils/sensitivePermissions';
import ProjectFocusCard from './components/ProjectFocusCard';
import AdditionDetailDrawer from '../../modules/progress/components/AdditionDetailDrawer';
import RectificationDetailDrawer from '../../modules/progress/components/RectificationDetailDrawer';

const { Title, Text } = Typography;
const { Option } = Select;

// 视图模式类型
type ViewMode = 'card' | 'compact' | 'table' | 'list';
type DashboardTab = 'overview' | 'risk';

// 视图模式配置
const VIEW_MODE_OPTIONS = [
  { value: 'card', icon: <ProjectOutlined />, label: '卡片' },
  { value: 'compact', icon: <AppstoreOutlined />, label: '紧凑' },
  { value: 'table', icon: <BarsOutlined />, label: '表格' },
  { value: 'list', icon: <UnorderedListOutlined />, label: '列表' }
];

// 项目状态配置
const PROJECT_STATUS_CONFIG = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin />, text: '进行中' },
  '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined />, text: '待开始' },
  '已暂停': { color: 'warning', icon: <ExclamationCircleOutlined />, text: '已暂停' }
};

// 本地存储键
const VIEW_MODE_STORAGE_KEY = 'dashboard_view_mode';

const getDaysRemainingLabel = (project: ProjectOverviewData) => {
  if (!project.endDate) {
    return '未设置结束日期';
  }

  if (project.scheduleStatusLabel) {
    return project.scheduleStatusLabel;
  }

  if (project.daysRemaining == null) {
    return '未设置结束日期';
  }

  if (project.daysRemaining > 0) {
    return `剩余 ${project.daysRemaining} 天`;
  }

  if (project.daysRemaining === 0) {
    return '今日到期';
  }

  return `已超期 ${Math.abs(project.daysRemaining)} 天`;
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(/,/g, '').replace(/%$/, '');
    if (!normalized) {
      return fallback;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value == null) {
    return null;
  }

  const parsed = toFiniteNumber(value, NaN);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatWan = (value: unknown) => `¥${(toFiniteNumber(value) / 10000).toFixed(1)}万`;

const Dashboard: React.FC = () => {
  const { checkPermission } = useAuthStore();
  const canViewBudget = hasSensitivePermission(checkPermission, 'budget');

  // 路由导航
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  // 响应式配置
  const { isMobile, isTablet, gridGutter } = useResponsiveConfig();

  // 视图模式状态（从本地存储读取默认值）
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved && ['card', 'compact', 'table', 'list'].includes(saved)) {
      return saved as ViewMode;
    }
    // 移动端默认使用列表视图，PC端默认使用高级卡片视图
    return isMobile ? 'list' : 'card';
  });

  // 项目筛选状态
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [activeTab, setActiveTab] = useState<DashboardTab>(() => {
    const tab = searchParams.get('tab');
    return tab === 'risk' || tab === 'control' ? 'risk' : 'overview';
  });
  
  // 项目概览弹窗状态
  const [overviewModalVisible, setOverviewModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectOverviewData | null>(null);
  const [projectFormVisible, setProjectFormVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  
  // 添加数据弹窗状态
  const [addDataModalVisible, setAddDataModalVisible] = useState(false);
  const [selectedProjectForAdd, setSelectedProjectForAdd] = useState<ProjectOverviewData | null>(null);

  // 工时表单弹窗状态
  const [workTimeFormVisible, setWorkTimeFormVisible] = useState(false);
  // 设备使用表单弹窗状态
  const [equipmentFormVisible, setEquipmentFormVisible] = useState(false);
  // 材料使用表单弹窗状态
  const [materialFormVisible, setMaterialFormVisible] = useState(false);
  const [selectedFocusItem, setSelectedFocusItem] = useState<DashboardFocusItem | null>(null);
  const [additionDrawerOpen, setAdditionDrawerOpen] = useState(false);
  const [rectificationDrawerOpen, setRectificationDrawerOpen] = useState(false);

  // 保存视图模式到本地存储
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // 移动端自动切换到列表视图
  useEffect(() => {
    if (isMobile && viewMode === 'table') {
      setViewMode('list');
    }
  }, [isMobile]);

  // 点击项目卡片 - 打开概览弹窗
  const handleProjectClick = (project: ProjectOverviewData) => {
    setSelectedProject(project);
    setOverviewModalVisible(true);
  };

  // 关闭概览弹窗
  const handleCloseOverviewModal = () => {
    setOverviewModalVisible(false);
    setSelectedProject(null);
  };

  const refreshProjectRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['projects-overview', 'management-page'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
  };

  const handleEditProject = async (project: ProjectOverviewData) => {
    try {
      const response = await ProjectService.getProjectById(project.id);
      if (response.success && response.data) {
        setEditingProject(response.data);
        setProjectFormVisible(true);
      } else {
        message.error(response.message || '获取项目详情失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || '获取项目详情失败');
    }
  };

  const handleProjectFormSuccess = () => {
    setProjectFormVisible(false);
    setEditingProject(null);
    refreshProjectRelatedQueries();
  };

  const handleProjectFormCancel = () => {
    setProjectFormVisible(false);
    setEditingProject(null);
  };

  // 导出项目数据
  const handleExport = async (project: ProjectOverviewData) => {
    try {
      message.loading({ content: '正在导出...', key: 'export' });
      navigate(`/reports/project-comprehensive?projectId=${project.id}&action=export`);
      message.success({ content: '已跳转到报表页面，请在报表页面进行导出', key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'export' });
    }
  };

  // 添加数据
  const handleAddData = (project: ProjectOverviewData) => {
    setSelectedProjectForAdd(project);
    setAddDataModalVisible(true);
  };

  // 添加数据选项
  const handleAddDataOption = (type: 'worktime' | 'equipment' | 'material') => {
    setAddDataModalVisible(false);
    if (!selectedProjectForAdd) return;

    // 关闭选择弹窗后直接打开对应的表单弹窗
    switch (type) {
      case 'worktime':
        setWorkTimeFormVisible(true);
        break;
      case 'equipment':
        setEquipmentFormVisible(true);
        break;
      case 'material':
        setMaterialFormVisible(true);
        break;
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    setActiveTab(tab === 'risk' || tab === 'control' ? 'risk' : 'overview');
  }, [searchParams]);

  const handleTabChange = (value: DashboardTab) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === 'risk') {
      next.set('tab', 'risk');
    } else {
      next.delete('tab');
    }
    setSearchParams(next);
  };

  // 获取Dashboard完整数据（使用聚合接口）
  const { 
    data: dashboardData, 
    isLoading: dashboardLoading, 
    error: dashboardError, 
    refetch: refetchDashboard 
  } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => DashboardService.getDashboardData(),
    staleTime: 0, // 每次都重新请求，不使用缓存
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: dashboardFocusData,
    isLoading: dashboardFocusLoading,
    refetch: refetchDashboardFocus
  } = useQuery({
    queryKey: ['dashboard', 'focus'],
    queryFn: () => StatisticsService.getDashboardFocusStream(),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // 根据筛选条件过滤项目
  const filteredProjects = React.useMemo(() => {
    if (!dashboardData?.projects) return [];
    let filtered = dashboardData.projects;
    
    // 按状态筛选
    if (projectFilter !== 'all') {
      filtered = filtered.filter(p => p.status === projectFilter);
    }
    
    // 按搜索关键词筛选
    if (searchInput.trim()) {
      const keyword = searchInput.trim().toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(keyword) || 
        (p as any).projectCode?.toLowerCase().includes(keyword)
      );
    }
    
    return filtered;
  }, [dashboardData?.projects, projectFilter, searchInput]);

  const summaryMetrics = React.useMemo(() => {
    const projects = dashboardData?.projects || [];
    const overview = dashboardData?.overview;
    const totalBudget = toNullableNumber(overview?.totalBudget);
    const totalActualCost = overview?.totalCost != null
      ? toFiniteNumber(overview.totalCost)
      : projects.reduce((sum, project) => sum + toFiniteNumber(project.actualCost), 0);
    const overBudgetCount = overview?.overBudgetProjects != null ? toFiniteNumber(overview.overBudgetProjects) : null;
    const activeProjectCount = overview?.activeProjects != null
      ? toFiniteNumber(overview.activeProjects)
      : projects.filter((project) => project.status === '进行中').length;

    return {
      totalProjects: overview?.totalProjects != null ? toFiniteNumber(overview.totalProjects) : projects.length,
      activeProjects: activeProjectCount,
      totalBudget,
      totalActualCost,
      totalBudgetUsage: toNullableNumber(overview?.totalBudgetUsageRate),
      overBudgetCount,
      noBudgetCount: overview?.noBudgetProjects != null ? toFiniteNumber(overview.noBudgetProjects) : null,
      overdueCount: overview?.overdueProjects != null
        ? toFiniteNumber(overview.overdueProjects)
        : projects.filter((project) => toFiniteNumber(project.daysRemaining) < 0).length,
      staleCount: overview?.staleProjects != null
        ? toFiniteNumber(overview.staleProjects)
        : projects.filter((project) => project.riskTags?.includes('长期无更新')).length,
    };
  }, [dashboardData]);

  const overviewHeroMetrics = React.useMemo(() => ([
    {
      key: 'budget',
      label: '总预算盘面',
      value: canViewBudget && summaryMetrics.totalBudget != null ? formatWan(summaryMetrics.totalBudget) : '--',
      detail: `${summaryMetrics.totalProjects} 个项目`,
      icon: <FundOutlined />,
      accent: '#f6c453'
    },
    {
      key: 'cost',
      label: '累计统计成本',
      value: formatWan(summaryMetrics.totalActualCost),
      detail: `${summaryMetrics.activeProjects} 个项目进行中`,
      icon: <DollarOutlined />,
      accent: '#65d6b8'
    },
    {
      key: 'usage',
      label: '预算使用率',
      value: canViewBudget && summaryMetrics.totalBudgetUsage != null ? `${summaryMetrics.totalBudgetUsage.toFixed(1)}%` : '--',
      detail: canViewBudget
        ? ((summaryMetrics.overBudgetCount ?? 0) > 0 ? `${summaryMetrics.overBudgetCount} 个项目超预算` : '整体预算可控')
        : '按权限隐藏',
      icon: <RiseOutlined />,
      accent: '#7a8cff'
    }
  ]), [summaryMetrics, canViewBudget]);

  const handleFocusItemClick = (item: DashboardFocusItem) => {
    setSelectedFocusItem(item);
    if (item.type === 'addition') {
      setAdditionDrawerOpen(true);
      setRectificationDrawerOpen(false);
    } else {
      setRectificationDrawerOpen(true);
      setAdditionDrawerOpen(false);
    }
  };

  const handleFocusViewMore = (type: 'addition' | 'rectification') => {
    navigate(type === 'addition' ? '/progress/additions' : '/progress/rectifications');
  };

  const handleFocusRefresh = async () => {
    await refetchDashboardFocus();
  };

  const dashboardTheme = React.useMemo(() => {
    const hasCriticalRisk = canViewBudget && ((summaryMetrics.totalBudgetUsage ?? 0) >= 100 || (summaryMetrics.overBudgetCount ?? 0) > 0);
    return {
      pageBackground: 'linear-gradient(180deg, #d7dfe5 0%, #c1ccd3 42%, #a6b4be 100%)',
      cardBackground: 'rgba(224, 230, 234, 0.82)',
      cardBackgroundElevated: 'rgba(214, 221, 226, 0.92)',
      panelBackground: 'rgba(232, 237, 240, 0.82)',
      panelBackgroundStrong: 'rgba(223, 229, 233, 0.92)',
      softBorder: '1px solid rgba(16, 27, 35, 0.08)',
      borderStrong: '1px solid rgba(16, 27, 35, 0.1)',
      textPrimary: '#10171d',
      textSecondary: '#455763',
      textMuted: '#667986',
      accentGold: '#b99258',
      accentBlue: '#547590',
      accentMint: '#5d8a7f',
      accentRed: '#c46d5f',
      shadow: '0 28px 60px rgba(46, 60, 70, 0.14)',
      accent: hasCriticalRisk ? '#c46d5f' : '#b99258',
      accentSoft: hasCriticalRisk ? 'rgba(196,109,95,0.14)' : 'rgba(185,146,88,0.14)',
      controlBackground: 'rgba(236, 240, 243, 0.88)',
      controlBackgroundStrong: 'rgba(228, 233, 237, 0.94)',
      controlBorder: '1px solid rgba(16, 27, 35, 0.08)',
    };
  }, [summaryMetrics.totalBudgetUsage, summaryMetrics.overBudgetCount]);

  const insightTags = React.useMemo(() => {
    const tags = [] as { label: string; value: string; icon: React.ReactNode; tone: string }[];

    tags.push({
      label: '活跃项目',
      value: `${summaryMetrics.activeProjects}/${summaryMetrics.totalProjects}`,
      icon: <ThunderboltOutlined />,
      tone: 'rgba(101, 214, 184, 0.16)'
    });

    tags.push({
      label: '超期项目',
      value: `${summaryMetrics.overdueCount} 个`,
      icon: <CalendarOutlined />,
      tone: 'rgba(122, 140, 255, 0.16)'
    });

    tags.push({
      label: '长期未更新',
      value: `${summaryMetrics.staleCount} 个`,
      icon: <WarningOutlined />,
      tone: 'rgba(246, 196, 83, 0.16)'
    });

    return tags;
  }, [summaryMetrics]);

  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (!projectId || !dashboardData?.projects?.length) return;

    const matchedProject = dashboardData.projects.find((project) => project.id === projectId || (project as any)._id === projectId);
    if (!matchedProject) {
      message.warning('未找到指定的项目，请检查项目ID');
      const next = new URLSearchParams(searchParams);
      next.delete('projectId');
      setSearchParams(next);
      return;
    }

    if (activeTab !== 'overview') {
      setActiveTab('overview');
    }

    setSelectedProject(matchedProject);
    setOverviewModalVisible(true);

    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    setSearchParams(next);
  }, [searchParams, dashboardData?.projects, activeTab, setSearchParams]);

  // 自动刷新控制（带冷却时间）
  const refreshState = useAutoRefresh({
    enabled: false, // 默认关闭自动刷新
    interval: 60000, // 60秒自动刷新
    manualRefreshCooldown: 10000, // 10秒手动刷新冷却
    onRefresh: async () => {
      await refetchDashboard();
    }
  });

  // 数据变化检测
  const overviewChangeDetector = useDataChangeDetector({
    data: dashboardData?.overview,
    enabled: true
  });

  if (dashboardError) {
    return (
      <ResponsiveContainer maxWidth="full" padding="none">
        <Title level={isMobile ? 4 : 2}>项目经营总览</Title>
        <Alert
          title="数据加载失败"
          description="无法连接到后端服务，请检查网络连接或联系管理员"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </ResponsiveContainer>
    );
  }

  // 渲染紧凑卡片视图
  const renderCompactCardView = () => (
    <Row gutter={[12, 12]}>
      {filteredProjects.map((project) => (
        <Col xs={12} sm={8} md={6} lg={6} xl={4} key={project.id}>
          <CompactProjectCard
            project={project}
            onClick={handleProjectClick}
            isMobile={isMobile}
          />
        </Col>
      ))}
    </Row>
  );

  // 渲染完整卡片视图
  const renderFullCardView = () => (
    <Row gutter={[gridGutter, gridGutter]}>
      {filteredProjects.map((project) => {
        const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
        const statusConfig = PROJECT_STATUS_CONFIG[project.status as keyof typeof PROJECT_STATUS_CONFIG] || PROJECT_STATUS_CONFIG['待开始'];
        const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);
        const usagePercent = project.budget > 0 ? (project.actualCost / project.budget) * 100 : 0;
        const progressValue = Math.max(0, Math.min(project.progress || 0, 100));
        const riskTags = (project.riskTags || []).slice(0, 3);

        return (
          <Col xs={24} sm={24} md={12} lg={8} key={project.id}>
            <Card
              hoverable
              size={isMobile ? 'small' : 'default'}
              style={{
                height: '100%',
                borderRadius: 24,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                background: `linear-gradient(180deg, ${budgetColors.backgroundColor} 0%, rgba(255,255,255,0.02) 100%), ${dashboardTheme.cardBackground}`,
                boxShadow: dashboardTheme.shadow,
                position: 'relative'
              }}
              styles={{ body: { padding: isMobile ? 16 : 24 } }}
              onClick={() => handleProjectClick(project)}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `radial-gradient(circle at top right, ${dashboardTheme.accentSoft} 0%, transparent 38%)`,
                  pointerEvents: 'none'
                }}
              />

              <Space orientation="vertical" size={20} style={{ width: '100%', position: 'relative' }}>
                {/* 头部区域：项目编码 + 名称 + 状态标签 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'inline-block',
                      color: dashboardTheme.accentBlue,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.16em',
                      background: 'rgba(84, 117, 144, 0.1)',
                      padding: '3px 8px',
                      borderRadius: 6,
                      marginBottom: 8,
                      textTransform: 'uppercase'
                    }}>
                      PROJECT / {((project as any).projectCode || 'UNSET').toString().toUpperCase()}
                    </div>
                    <Title
                      level={isMobile ? 5 : 4}
                      style={{
                        margin: '0 0 6px',
                        color: dashboardTheme.textPrimary,
                        lineHeight: 1.3
                      }}
                    >
                      {project.name}
                    </Title>
                    <Text style={{ color: dashboardTheme.textMuted, fontSize: 12 }}>
                      最近更新：{project.recentActivity || '暂无动态'}
                    </Text>
                  </div>
                  <Tag
                    color={statusConfig.color}
                    icon={statusConfig.icon}
                    style={{ borderRadius: 999, paddingInline: 10, marginInlineEnd: 0 }}
                  >
                    {statusConfig.text}
                  </Tag>
                </div>

                {/* 双列信息区：预算燃耗 + 进度完成度 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr',
                    gap: 16,
                    alignItems: 'stretch'
                  }}
                >
                  {/* 预算燃耗区块 */}
                  <div
                    style={{
                      padding: 18,
                      borderRadius: 20,
                      background: 'rgba(255,255,255,0.05)',
                      border: dashboardTheme.softBorder,
                      backdropFilter: 'blur(14px)'
                    }}
                  >
                    <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={{ color: dashboardTheme.textSecondary, fontSize: 13 }}>预算燃耗</Text>
                        <Text style={{ color: health.color, fontWeight: 700, fontSize: 14 }}>{usagePercent.toFixed(1)}%</Text>
                      </div>
                      <Progress
                        percent={Math.min(usagePercent, 100)}
                        strokeColor={health.color}
                        railColor="rgba(255,255,255,0.08)"
                        size="small"
                        showInfo={false}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <div>
                          <Text style={{ color: dashboardTheme.textMuted, fontSize: 11 }}>预算</Text>
                          <div style={{ color: dashboardTheme.textPrimary, fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
                            {formatWan(project.budget || 0)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Text style={{ color: dashboardTheme.textMuted, fontSize: 11 }}>成本</Text>
                          <div style={{ color: dashboardTheme.textPrimary, fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
                            {formatWan(project.actualCost || 0)}
                          </div>
                        </div>
                      </div>
                    </Space>
                  </div>

                  {/* 进度完成度区块 */}
                  <div
                    style={{
                      padding: 18,
                      borderRadius: 20,
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                      border: dashboardTheme.softBorder,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <Text style={{ color: dashboardTheme.textSecondary, fontSize: 13 }}>进度完成度</Text>
                      <div style={{ color: dashboardTheme.textPrimary, fontSize: 32, fontWeight: 700, lineHeight: 1.1, marginTop: 6 }}>
                        {progressValue}%
                      </div>
                    </div>
                    <Progress
                      percent={progressValue}
                      strokeColor="#7a8cff"
                      railColor="rgba(255,255,255,0.08)"
                      size="small"
                      showInfo={false}
                    />
                  </div>
                </div>

                {/* 风险标签区 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {riskTags.length > 0 ? riskTags.map((tag) => (
                    <Tag
                      key={tag}
                      style={{
                        marginInlineEnd: 0,
                        borderRadius: 999,
                        paddingInline: 10,
                        color: dashboardTheme.textPrimary,
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}
                    >
                      {tag}
                    </Tag>
                  )) : (
                    <Tag
                      style={{
                        marginInlineEnd: 0,
                        borderRadius: 999,
                        paddingInline: 10,
                        color: '#65d6b8',
                        background: 'rgba(101,214,184,0.14)',
                        border: '1px solid rgba(101,214,184,0.18)'
                      }}
                    >
                      预算健康
                    </Tag>
                  )}
                </div>

                {/* 底部统计指标区 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 12
                  }}
                >
                  {[
                    {
                      label: '执行状态',
                      value: health.status,
                      icon: <ThunderboltOutlined style={{ color: dashboardTheme.accent }} />
                    },
                    {
                      label: '统计记录',
                      value: `${project.teamSize || 0} 条`,
                      icon: <TeamOutlined style={{ color: '#65d6b8' }} />
                    },
                    {
                      label: '计划节点',
                      value: getDaysRemainingLabel(project),
                      icon: <CalendarOutlined style={{ color: '#7a8cff' }} />
                    }
                  ].map((item) => (
                    <div
                      key={item.label}
                      style={{
                        padding: 12,
                        borderRadius: 16,
                        background: 'rgba(255,255,255,0.04)',
                        border: dashboardTheme.softBorder
                      }}
                    >
                      <Space size={8} align="start">
                        {item.icon}
                        <div>
                          <div style={{ color: dashboardTheme.textMuted, fontSize: 11 }}>{item.label}</div>
                          <div style={{ color: dashboardTheme.textPrimary, fontSize: 12, fontWeight: 600, marginTop: 3 }}>
                            {item.value}
                          </div>
                        </div>
                      </Space>
                    </div>
                  ))}
                </div>

                {/* 操作按钮区 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, color: dashboardTheme.textPrimary, fontSize: 13 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProjectClick(project);
                    }}
                  >
                    查看经营详情 <RightOutlined />
                  </Button>
                  <Space size={8}>
                    <Button
                      size="small"
                      style={{ borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: dashboardTheme.textPrimary, border: '1px solid rgba(255,255,255,0.08)' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(project);
                      }}
                    >
                      导出
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      style={{ borderRadius: 999, background: dashboardTheme.accent, borderColor: dashboardTheme.accent, color: '#151515' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddData(project);
                      }}
                    >
                      添加数据
                    </Button>
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // 渲染表格视图
  const renderTableView = () => (
    <ProjectTableView
      projects={filteredProjects}
      loading={dashboardLoading}
      onViewDetails={handleProjectClick}
      onExport={handleExport}
      onAddData={handleAddData}
      isMobile={isMobile}
    />
  );

  // 渲染列表视图
  const renderListView = () => (
    <CollapsibleProjectList
      projects={filteredProjects}
      onViewDetails={handleProjectClick}
      onExport={handleExport}
      onAddData={handleAddData}
    />
  );

  // 根据视图模式渲染项目列表
  const renderProjectList = () => {
    if (dashboardLoading) {
      return <ProjectSkeleton count={isMobile ? 4 : 8} isMobile={isMobile} viewMode={viewMode} />;
    }

    if (!filteredProjects || filteredProjects.length === 0) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <ProjectOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} type="secondary">暂无项目</Title>
            <Text type="secondary">
              {projectFilter === 'all' ? '还没有创建任何项目' : `没有${projectFilter}的项目`}
            </Text>
            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={() => navigate('/project')}>创建新项目</Button>
            </div>
          </div>
        </Card>
      );
    }

    switch (viewMode) {
      case 'compact':
        return renderCompactCardView();
      case 'card':
        return renderFullCardView();
      case 'table':
        return renderTableView();
      case 'list':
        return renderListView();
      default:
        return renderCompactCardView();
    }
  };

  if (activeTab === 'risk') {
    return (
      <ResponsiveContainer maxWidth="full" padding="none">
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <Card size={isMobile ? 'small' : 'default'}>
            <Segmented
              value={activeTab}
              onChange={(value) => handleTabChange(value as DashboardTab)}
              options={[
                { value: 'overview', label: '总览' },
                { value: 'risk', label: '风险' }
              ]}
              size={isMobile ? 'small' : 'middle'}
            />
          </Card>
          <ProjectControlCenter embedded />
        </Space>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer maxWidth="full" padding="none">
      <div
        style={{
          background: dashboardTheme.pageBackground,
          borderRadius: isMobile ? 24 : 32,
          padding: isMobile ? 16 : 28,
          boxShadow: dashboardTheme.shadow,
          border: dashboardTheme.softBorder,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top left, rgba(84,117,144,0.14) 0%, transparent 26%), radial-gradient(circle at 85% 15%, rgba(185,146,88,0.14) 0%, transparent 20%)',
            pointerEvents: 'none'
          }}
        />
        <Space orientation="vertical" size="large" style={{ width: '100%', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            padding: isMobile ? 14 : 18,
            borderRadius: 22,
            background: dashboardTheme.controlBackgroundStrong,
            border: dashboardTheme.softBorder,
            backdropFilter: 'blur(10px)'
          }}
        >
          <Segmented
            value={activeTab}
            onChange={(value) => handleTabChange(value as DashboardTab)}
            options={[
              { value: 'overview', label: '总览' },
              { value: 'risk', label: '风险' }
            ]}
            size={isMobile ? 'small' : 'middle'}
            style={{
              background: dashboardTheme.controlBackground,
              padding: 4,
              borderRadius: 14,
              border: dashboardTheme.controlBorder
            }}
          />
          <RefreshControl
            refreshState={refreshState}
            showAutoRefresh={!isMobile}
            showLastRefreshTime={true}
            size={isMobile ? 'small' : 'middle'}
          />
        </div>

        {/* 数据变化提示 */}
        {overviewChangeDetector.hasChanges && (
          <DataChangeIndicator
            hasChanges={overviewChangeDetector.hasChanges}
            onViewChanges={() => {
              overviewChangeDetector.acknowledgeChanges();
            }}
          />
        )}

        {/* 总体统计卡片 */}
        {dashboardLoading ? (
          <Row gutter={[gridGutter, gridGutter]}>
            {[1, 2, 3].map((i) => (
              <Col xs={24} md={8} key={i}>
                <Card
                  loading
                  style={{
                    borderRadius: 24,
                    background: dashboardTheme.cardBackground,
                    border: dashboardTheme.softBorder,
                    boxShadow: dashboardTheme.shadow
                  }}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.4fr) minmax(320px, 0.9fr)',
              gap: gridGutter
            }}
          >
            <Card
              variant="borderless"
              style={{
                borderRadius: 28,
                background: dashboardTheme.cardBackgroundElevated,
                border: dashboardTheme.softBorder,
                boxShadow: dashboardTheme.shadow,
                overflow: 'hidden'
              }}
              styles={{ body: { padding: isMobile ? 20 : 28 } }}
            >
              <Space orientation="vertical" size={22} style={{ width: '100%' }}>
                <div>
                  <Title level={isMobile ? 3 : 1} style={{ color: dashboardTheme.textPrimary, margin: '10px 0 10px' }}>
                    项目经营总览
                  </Title>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {insightTags.map((item) => (
                    <div
                      key={item.label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        borderRadius: 999,
                        background: item.tone,
                        color: dashboardTheme.textPrimary,
                        border: dashboardTheme.softBorder
                      }}
                    >
                      <span style={{ display: 'inline-flex' }}>{item.icon}</span>
                      <span style={{ fontSize: 13 }}>{item.label}</span>
                      <span style={{ fontWeight: 700 }}>{item.value}</span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
                    gap: 14
                  }}
                >
                  {overviewHeroMetrics.map((metric) => (
                    <div
                      key={metric.key}
                      style={{
                        padding: isMobile ? 16 : 18,
                        borderRadius: 22,
                        background: 'rgba(232, 237, 240, 0.86)',
                        border: dashboardTheme.softBorder,
                        minHeight: 132
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <Text style={{ color: dashboardTheme.textSecondary }}>{metric.label}</Text>
                        <span
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 12,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0d1320',
                            background: metric.accent,
                            boxShadow: `0 12px 24px ${metric.accent}33`
                          }}
                        >
                          {metric.icon}
                        </span>
                      </div>
                      <div style={{ color: dashboardTheme.textPrimary, fontSize: isMobile ? 24 : 32, fontWeight: 700, lineHeight: 1.1 }}>
                        {metric.value}
                      </div>
                      <div style={{ color: dashboardTheme.textMuted, fontSize: 13, marginTop: 10 }}>
                        {metric.detail}
                      </div>
                    </div>
                  ))}
                </div>
              </Space>
            </Card>

            <ProjectFocusCard
              additions={dashboardFocusData?.additions || []}
              rectifications={dashboardFocusData?.rectifications || []}
              loading={dashboardFocusLoading}
              onItemClick={handleFocusItemClick}
              onViewMore={handleFocusViewMore}
            />
          </div>
        )}

        <Card
          variant="borderless"
          style={{
            borderRadius: 28,
            background: dashboardTheme.cardBackground,
            border: dashboardTheme.softBorder,
            boxShadow: dashboardTheme.shadow
          }}
          styles={{ body: { padding: isMobile ? 18 : 24 } }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 14
            }}
          >
            <Space size={12} wrap>
              <Text strong style={{ fontSize: isMobile ? 15 : 18, color: dashboardTheme.textPrimary }}>
                项目池 · 业务清单
              </Text>
              <Input
                placeholder="搜索项目名称或编码"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  width: isMobile ? '100%' : 260,
                  background: dashboardTheme.controlBackground,
                  borderRadius: 12,
                  border: dashboardTheme.controlBorder,
                  color: dashboardTheme.textPrimary
                }}
                size={isMobile ? 'small' : 'middle'}
                allowClear
              />
              <Select
                value={projectFilter}
                onChange={setProjectFilter}
                style={{
                  width: isMobile ? 110 : 126,
                  borderRadius: 12
                }}
                size={isMobile ? 'small' : 'middle'}
                styles={{
                  popup: {
                    root: {
                      background: '#111827',
                      borderRadius: 14,
                      border: '1px solid rgba(255,255,255,0.08)'
                    }
                  }
                }}
              >
                <Option value="all">全部项目</Option>
                <Option value="进行中">进行中</Option>
                <Option value="已完成">已完成</Option>
                <Option value="待开始">待开始</Option>
                <Option value="已暂停">已暂停</Option>
              </Select>
            </Space>

            <Space size={12} wrap>
              <Segmented
                value={viewMode}
                onChange={(value) => setViewMode(value as ViewMode)}
                options={VIEW_MODE_OPTIONS.filter(opt => !(isMobile && opt.value === 'table')).map(opt => ({
                  value: opt.value,
                  icon: opt.icon,
                  title: opt.label
                }))}
                size={isMobile ? 'small' : 'middle'}
                style={{
                  background: dashboardTheme.controlBackground,
                  padding: 4,
                  borderRadius: 14,
                  border: dashboardTheme.controlBorder
                }}
              />
              <Text style={{ color: dashboardTheme.textSecondary, fontSize: 12 }}>
                共 {filteredProjects.length} 个项目，可继续按状态与视图快速下钻
              </Text>
            </Space>
          </div>
        </Card>

        {/* 项目列表 */}
        {renderProjectList()}
      </Space>
      </div>

      {/* 项目概览弹窗 */}
      <ProjectOverviewModal
        visible={overviewModalVisible}
        project={selectedProject}
        onClose={handleCloseOverviewModal}
        onExport={handleExport}
        onEditProject={handleEditProject}
      />

      <Modal
        title={editingProject ? `编辑项目信息 · ${editingProject.name}` : '编辑项目信息'}
        open={projectFormVisible}
        onCancel={handleProjectFormCancel}
        footer={null}
        width={760}
        destroyOnHidden
      >
        <ProjectForm
          project={editingProject}
          onSubmit={handleProjectFormSuccess}
          onCancel={handleProjectFormCancel}
        />
      </Modal>

      {/* 添加数据弹窗 */}
      <Modal
        title={`添加数据 - ${selectedProjectForAdd?.name || ''}`}
        open={addDataModalVisible}
        onCancel={() => setAddDataModalVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '16px 0' }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Button
              block
              size="large"
              icon={<ClockCircleOutlined />}
              onClick={() => handleAddDataOption('worktime')}
            >
              新增出工记录
            </Button>
            <Button
              block
              size="large"
              icon={<ToolOutlined />}
              onClick={() => handleAddDataOption('equipment')}
            >
              添加设备使用记录
            </Button>
            <Button
              block
              size="large"
              icon={<ShoppingOutlined />}
              onClick={() => handleAddDataOption('material')}
            >
              添加材料使用记录
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 工时记录表单弹窗 */}
      <Modal
        title="新增出工记录"
        open={workTimeFormVisible}
        onCancel={() => setWorkTimeFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <WorkerHistoryForm
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setWorkTimeFormVisible(false);
            message.success('出工记录添加成功');
          }}
          onCancel={() => setWorkTimeFormVisible(false)}
        />
      </Modal>

      {/* 设备使用记录表单弹窗 */}
      <Modal
        title="添加设备使用记录"
        open={equipmentFormVisible}
        onCancel={() => setEquipmentFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <EquipmentUsageForm
          initialValues={null}
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setEquipmentFormVisible(false);
          }}
          onCancel={() => setEquipmentFormVisible(false)}
        />
      </Modal>

      {/* 材料使用记录表单弹窗 */}
      <Modal
        title="添加材料使用记录"
        open={materialFormVisible}
        onCancel={() => setMaterialFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <MaterialUsageForm
          initialValues={null}
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setMaterialFormVisible(false);
          }}
          onCancel={() => setMaterialFormVisible(false)}
        />
      </Modal>

      <AdditionDetailDrawer
        open={additionDrawerOpen}
        additionId={selectedFocusItem?.type === 'addition' ? selectedFocusItem.id : undefined}
        onClose={() => setAdditionDrawerOpen(false)}
        onSuccess={handleFocusRefresh}
      />

      <RectificationDetailDrawer
        open={rectificationDrawerOpen}
        rectificationId={selectedFocusItem?.type === 'rectification' ? selectedFocusItem.id : undefined}
        onClose={() => setRectificationDrawerOpen(false)}
        onSuccess={handleFocusRefresh}
      />
    </ResponsiveContainer>
  );
};

export default Dashboard;
